import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import * as util from "@/service/util";
import moment from "moment";
import * as reportService from "@/service/reportService";
/**
 * Xuất dữ liệu JSON ra file Excel
 * @param {Array} data - Dữ liệu dạng mảng object [{...}, {...}]
 * @param {string} fileName - Tên file muốn lưu (không cần .xlsx)
 * @param {string} sheetName - Tên sheet trong Excel
*/
export async function exportExcelFile(data, fileName, sheetName) {
  ///aoa_to_sheet: dữ liệu mảng 2 chiều
  const worksheet = XLSX.utils.aoa_to_sheet(data);

  ///Điều chỉnh độ rộng của cột chứa data
  const firstRow = data[0].length > 1 ? data[0] : data[1] || [];
  const colWidths = firstRow.map((_: any, colIndex: number) => {
    const maxWidth = data.reduce((max, row) => {
      const cell = row[colIndex];
      return Math.max(max, cell ? cell.toString().length : 10);
    }, 10);
    return { wch: maxWidth + 3 };
  });
  worksheet["!cols"] = colWidths;

  ///
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([excelBuffer], {
    type:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${fileName}.xlsx`);
}
/**
 * Xuất dữ liệu csv ra file Excel
 * @param {string} rawData - Dữ liệu dạng csv từ BE
 * @param {string} rawFileName - Tên file
 */
export async function csvExportExcel(rawData, rawFileName) {
  const workbook = XLSX.read(rawData, { type: 'string', raw: true, codepage: 65001 });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];

  const newWorkbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(newWorkbook, worksheet);

  const excelBuffer = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

  saveAs(blob, `${rawFileName}.xlsx`);
}
/**
 * Hàm xuất dữ liệu ra file .txt
 * @param {string} fileName - tên file
 * @param {string | string[]} content - nội dung csv được trả về từ api
 */
export async function exportTxtFile(fileName, content): Promise<void> {
  const blob = new Blob(Array.isArray(content) ? content : [content], {
    type: "text/plain",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName.endsWith(".txt") ? fileName : `${fileName}.txt`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Hàm xuất dữ liệu ra file Word
 * @param {string} fileName - tên file
 * @param {string} htmlContent - nội dung HTML cần xuất
 */
export async function exportDocxFile(fileName, htmlContent, customStyles: string = ""): Promise<void> {
  const mhtml = `MIME-Version: 1.0
Content-Type: multipart/related; boundary="NEXT_PART_BOUNDARY"

--NEXT_PART_BOUNDARY
Content-Type: text/html; charset="utf-8"
Content-Location: file:///main.html

<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${fileName}</title>
  <style>
    body { font-family: 'Times New Roman', serif; font-size: 12pt; line-height: 1.5; color: #000000; }
    h1, h2, h3, h4, h5, h6 { font-family: 'Times New Roman', serif; color: #000000; font-weight: bold; margin-top: 14pt; margin-bottom: 6pt; }
    h1 { font-size: 16pt; }
    h2 { font-size: 14pt; }
    h3 { font-size: 12pt; }
    p { margin: 0 0 6pt 0; }
    ul, ol { margin-top: 0; margin-bottom: 6pt; padding-left: 20pt; }
    li { margin-bottom: 3pt; }
    code { font-family: 'Courier New', monospace; font-size: 10pt; }
    strong { color: #000000; }
    ${customStyles}
  </style>
</head>
<body>
  ${htmlContent}
</body>
</html>

--NEXT_PART_BOUNDARY--`;

  const blob = new Blob([mhtml], {
    type: "application/msword;charset=utf-8"
  });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  // Sử dụng đuôi .doc để tránh cảnh báo file không hợp lệ (unreadable content) trên Word
  a.download = fileName.endsWith(".doc") ? fileName : `${fileName}.doc`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Xuất file tóm tắt cuộc họp (summaryArry: mảng chuỗi tóm tắt)
 * @param tFunc - hàm $t để dịch
 * @param {string} meetingName - tên cuộc họp
 * @param {string[]} summaryArry - nội dung tóm tắt api trả về
 */
export async function exportMeetingSummary(meeting: any, summaryArry: string[]): Promise<void> {
  if (!meeting) return;

  // Build sections: heading + markdown blocks
  const sections: util.DocSection[] = [
    { type: 'heading', text: 'II. Nội dung' },
  ];

  for (let index = 0; index < summaryArry.length; index++) {
    if (summaryArry.length > 1) {
      const label = util.SUMMARY_TYPE_LABELS[index] ?? `Phần ${index + 1}`;
      sections.push({ type: 'html', html: `<h2>${label}</h2>` });
    }
    sections.push({ type: 'markdown', content: summaryArry[index] });
  }

  const htmlContent = util.buildMeetingDocHtml({
    title: 'BIÊN BẢN CUỘC HỌP',
    meeting,
    sections,
  });

  const mName = util.convertMeetingName2FileName(meeting.name);
  await exportDocxFile(`MeetingMinutes_${mName}`, htmlContent);
}

/**
 * Xuất file nội dung cuộc họp (TRANSCRIPT)
 *  @param {object} meeting - đối tượng cuộc họp
 *  @param {string} contentRoom - tóm tắt nội dung cuộc họp do api trả về
 */
export async function exportMeetingContent(meeting: any, contentRoom: string): Promise<void> {
  if (!meeting || !contentRoom) return;
  const mName = util.convertMeetingName2FileName(meeting.name);

  const htmlContent = util.buildMeetingDocHtml({
    title: 'TRANSCRIPT',
    meeting,
    sections: [
      { type: 'heading', text: 'II. Nội dung' },
      { type: 'html', html: `<div>${util.convertTitleContentMeeting(contentRoom, true)}</div>` },
    ],

  });

  await exportDocxFile(`Transcript_${mName}`, htmlContent);
}

/**
 * Xuất file tóm tắt theo timeline
 * @param {object} meeting - đối tượng cuộc họp
 * @param {Array} summaryByTimeline - Danh sách tóm tắt theo timeline
 */
export async function exportMeetingSummaryTimeLine(meeting: any, summaryByTimeline: any[]) {
  if (!meeting || !summaryByTimeline || summaryByTimeline.length === 0) return;

  const mName = util.convertMeetingName2FileName(meeting.name);

  const htmlContent = util.buildMeetingDocHtml({
    title: 'NỘI DUNG TÓM TẮT THEO DÒNG THỜI GIAN',
    meeting,
    sections: [
      { type: 'heading', text: 'II. Nội dung' },
      { type: 'timeline', items: summaryByTimeline },
    ],
  });

  await exportDocxFile(`SummaryTimeline_${mName}`, htmlContent);
}

/**
 * Xuất file tóm tắt của tất cả người tham gia
 * @param {object} meeting - đối tượng cuộc họp
 * @param {Array} summaryAllUser - Mảng tóm tắt của tất cả người tham gia
 */
export async function exportAllUserSummary(meeting: any, summaryAllUser: any[]) {
  if (!meeting || !summaryAllUser?.length) return;
  const mName = util.convertMeetingName2FileName(meeting.name);

  const htmlContent = util.buildMeetingDocHtml({
    title: 'NỘI DUNG TÓM TẮT CỦA NGƯỜI THAM GIA',
    meeting,
    sections: [
      { type: 'heading', text: 'II. Nội dung' },
      { type: 'userlist', items: summaryAllUser },
    ],
  });

  await exportDocxFile(`SummaryUser_${mName}`, htmlContent);
}

////xuất các file excel
/**
 * @param customers - Danh sách khách hàng
 * @param tFunc - key dịch đa ngôn ngữ
 * @returns 
 */

export async function exportCustomerListExcel(customers: any[], tFunc: (key: string) => string): Promise<any> {

  if (!customers?.length) return;

  const sheetData = customers.map((c) => [
    c.id?.toString() ?? "",
    c.customerCode,
    c.previousOrgCode,
    c.orgCode,
    c.name,
    c.phoneNumber,
    c.email,
    c.address,
    c.saleAreaName,
    c.createdBy,
    new Date(c.createdTime).toLocaleDateString("en-GB"),
    c.subPlanName,
    new Date(c.cusSubStartDate).toLocaleDateString("en-GB"),
    new Date(c.cusSubEndDate).toLocaleDateString("en-GB"),
    c.remainingDays,
    c.customerSource,
    c.totalMeetings,
    c.totalMeetingsEnded,
    c.totalMeetingsCancelled,
    c.totalMeetingsDeleted,
  ]);

  // Dòng tiêu đề
  sheetData.unshift([
    "",
    tFunc("customerId"),
    tFunc("TTKD"),
    tFunc("ttkd2Cap"),
    tFunc("name"),
    tFunc("phoneNumber"),
    tFunc("email"),
    tFunc("address"),
    tFunc("saleArea"),
    tFunc("createBy"),
    tFunc("thoidiemtao"),
    tFunc("goicuoc"),
    tFunc("startDate"),
    tFunc("endDate"),
    tFunc("songayconhieuluc"),
    tFunc("loaitaikhoan"),
    tFunc("socuochopdatao"),
    tFunc("socuochopdaketthuc"),
    tFunc("socuochopdahuy"),
    tFunc("socuochopdaxoa"),
  ]);

  await exportExcelFile(sheetData, "DanhSachKhachHang_VNPTGOMEET", "DS_KhachHang");
}

/**
 * Xuất danh sách người tham gia cuộc họp ra Excel
 * @param { any[] } peers - danh sách người tham gia
 * @param { string } meetingName - tên cuộc họp
 */

export async function exportParticipantJoinMeeting(peers, meetingName) {
  const sheetData = peers.map(peer => [
    peer.uname,
    peer.peerId,
    peer.roleStr || "Tham gia",
    peer.logTime,
    peer.lastLogTime,
    peer.durationStr,
  ]);

  // Tiêu đề cột xuất excel
  sheetData.unshift([
    "Tên",
    "ID",
    "Vai Trò",
    "Thời gian vào",
    "Thời gian ra",
    "Tổng tham gia",
  ]);

  const fileName = `DSThamGia_${util.convertMeetingName2FileName(meetingName)}`;
  await exportExcelFile(sheetData, fileName, "Danh sách tham gia");
}
/**
 * Xuất danh sách thao tác người dùng trong cuộc họp
 * @param {Array} peersExportfilelog - Dữ liệu log thao tác từ API
 * @param {string} meetingName - Tên cuộc họp
 */
export async function exportLogParticipantExcel(peersExportfilelog, meetingName) {
  if (!Array.isArray(peersExportfilelog)) return;

  // Xử lý dữ liệu
  const sheetData = peersExportfilelog.map(log => [
    log.displayName,
    log.id,
    log.roleStr || "Tham gia",
    log.sessionToken,
    log.logDate ? log.logDate.split(" ")[0] : "",
    log.logDate ? log.logDate.split(" ")[1] : "",
    log.lastLogDate ? log.lastLogDate.split(" ")[1] : "",
    log.durationStr,
    log.actions
      ?.map((a, actionIndex) => {
        const timeString = new Date(a.timestamp).toLocaleTimeString("vi-VN", { hour12: false });
        let actionString = `${timeString} - ${a.desc}`;
        if (a.peerId !== null && a.peerId !== undefined) {
          actionString += ` (${a.peerId})`;
        }
        actionString += ";";
        if (actionIndex === 0 && /^\d/.test(actionString)) {
          actionString = `'${actionString}`; // Thêm dấu ' nếu bắt đầu bằng số
        }
        return `\t${actionString}`;
      })
      .join("\n") || ""
  ]);

  // Tiêu đề các cột
  sheetData.unshift([
    "Tên",
    "ID",
    "Vai Trò",
    "Session",
    "Ngày họp",
    "Thời gian vào",
    "Thời gian ra",
    "Tổng tham gia",
    "Lịch sử thao tác"
  ]);

  // Xuất file Excel
  const fileName = `LogThaoTac_${util.convertMeetingName2FileName(meetingName)}`;
  await exportExcelFile(sheetData, fileName, "Thao tác trong CH");
}

/**
 * Xuất danh sách cuộc họp trong dashboard ra file CSV
 * @param {object} requestParams - Tham số request gửi API
 * @param {string} searchText - Từ khóa tìm kiếm
 * @param {string} filterStatus - Trạng thái lọc
 * @param {string | Date} fromDateFile - Ngày bắt đầu (ISO string hoặc Date)
 * @param {string | Date} toDateFile - Ngày kết thúc (ISO string hoặc Date)
 */
export async function exportMeetingListCSV(requestParams, searchText, filterStatus, fromDateFile, toDateFile) {
  try {
    // Lấy dữ liệu thô từ API
    const rawCsvData = await reportService.getMeetingListDashboard_ExportExcel(requestParams);

    // Mảng để ghép tên file
    const parts: string[] = [];

    if (searchText) parts.push(searchText);
    if (filterStatus) parts.push(filterStatus);

    // Format ngày tháng khi xuất file
    if (fromDateFile && fromDateFile !== "Invalid date") {
      parts.push(moment(fromDateFile).format("YYYY-MM-DD"));
    }

    if (toDateFile && toDateFile !== "Invalid date") {
      parts.push(moment(toDateFile).subtract(1, "days").format("YYYY-MM-DD"));
    }

    const rawName = parts.join("_");
    const rawFileName = util.convertMeetingName2FileName(rawName);
    const fileName = `DSCuocHop_${rawFileName}`;

    // Xuất file CSV
    await csvExportExcel(rawCsvData, fileName);

  } catch (error) {
    this.toastError(error);
  }
}

/**
 * Xuất danh sách danh bạ của khách hàng
 * @param {any[]} contacts - Dữ liệu danh bạ từ API
 */
export async function exportContactList(contacts) {
  if (!Array.isArray(contacts) || contacts.length === 0) {
    return;
  }

  // map data
  const sheetData = contacts.map((it) => [
    it.name,
    it.title,
    it.department,
    it.organization,
    it.phone,
    it.email,
  ]);

  // Tiêu đề cột
  sheetData.unshift([
    "Họ và tên",
    "Chức danh",
    "Phòng ban",
    "Đơn vị",
    "Di động",
    "Email",
  ]);

  //  export Excel
  await exportExcelFile(sheetData, "DanhSachLienHe", "DS_LienHe");
}

/**
 * Xuất danh sách tài khoản con của khách hàng
 * @param {any[]} users - Danh sách tài khoản con
 */
export async function exportUserList(users) {
  if (!Array.isArray(users) || users.length === 0) {
    return;
  }
  const sheetData = users.map(it => [
    it.name, it.username, it.phone, it.email, it.role
  ]);
  sheetData.unshift(["Họ và tên", "Tên đăng nhập", "Di động", "Email", "Vai trò"]);
  await exportExcelFile(sheetData, "DanhSachTaiKhoan", "DS_TaiKhoan");
}